// GTM, pre-login page loads only. As an index.html snippet it ran on every
// authenticated page too, which let anyone with container publish rights run JS
// next to the session token and the card forms, with no PR. It earns its keep on
// signup conversion tracking, which happens before login, so gating on "no session
// yet" keeps that and drops the rest.
//
// Reads storage via getStoredAuthEmail, not getLocalAuth() — this runs at
// bootstrap and must not pull the v2/utils barrel in behind it.
import { GTM_CONTAINER_ID, isDev, isLocalhost, isProd } from 'v2/configs/constants'
import { getStoredAuthEmail } from './internal-user'

const GTM_SCRIPT_ID = 'gtm-container'

// Storage failures (Safari Lockdown Mode, some WebViews — SER-6066) read as
// signed-out and load GTM. Deliberate: worst case is an extra tracking load.
const hasSession = (): boolean => getStoredAuthEmail() !== ''

// Idempotent. Separate from the gate so tests can call it directly.
export const injectGtm = () => {
  if (!GTM_CONTAINER_ID || document.getElementById(GTM_SCRIPT_ID)) return

  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

    const script = document.createElement('script')
    script.id = GTM_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`
    document.head.appendChild(script)
  } catch {
    // Swallow: a failed marketing tag must never break app bootstrap. Nothing to
    // report it to either — this runs before Sentry.init further down the module.
  }
}

// isLocalhost is new: as a snippet GTM had no env gate, so dev machines fed the
// same GA4 property and Ads audiences as real traffic.
export const loadGtm = () => {
  if (!(isDev || isProd) || isLocalhost) return
  if (hasSession()) return
  injectGtm()
}

// NOT a security boundary — read this before relying on it.
//
// Removing the tag does not revoke code the container already ran: its listeners,
// timers and any scripts it injected keep running. No login path reloads the
// document (completeLoginProcess and sso-redirect both stay in the same one), so a
// session that starts on /login carries whatever GTM already executed into the
// authenticated app.
//
// This reduces exposure, it does not eliminate it. The real fix is a fresh
// document on login, which has to land after setAuthCookie in completeLoginProcess
// plus sso-redirect separately, and needs testing across 2FA/SSO/invite/signup —
// too much to ride along with a PCI change. Tracked on SER-6170.
export const unloadGtm = () => {
  try {
    document.getElementById(GTM_SCRIPT_ID)?.remove()
    // Emptied, not undefined. By this file's own admission the container's
    // already-executed code keeps running after this, and any tag or CMP that
    // re-reads window.dataLayer and pushes would hit "Cannot read properties of
    // undefined" on a call stack this try/catch cannot cover — surfacing as a new
    // Sentry error inside the authenticated app. An empty array isolates the
    // container just as well without the crash.
    window.dataLayer = []
  } catch {
    // Never let analytics teardown break the login transition.
  }
}
